import { createContext, useContext } from 'react';
import type { PaymentMethodSummary, WalletBalance } from '@/shared/types';

export type WalletContextValue = {
  // Current balance, or null before the first load resolves / while unauthenticated.
  balance: WalletBalance | null;
  // The saved card, or null when none is on file yet.
  paymentMethod: PaymentMethodSummary | null;
  // True while the initial balance + payment-method fetch is in flight.
  loading: boolean;
  // Set when the initial load failed (network / billing outage); null otherwise.
  error: string | null;
  // Re-fetch the balance (e.g. after a top-up settles). Returns the fresh value so
  // callers can poll for a webhook-driven change without reading provider state.
  refreshBalance: () => Promise<WalletBalance | null>;
  // Re-fetch the saved card (e.g. after confirmCardSetup + its persisting webhook).
  refreshPaymentMethod: () => Promise<PaymentMethodSummary | null>;
  // Re-run the initial balance + payment-method load (used by the page's "Retry"
  // affordance after a load error).
  reload: () => Promise<void>;
};

export const WalletContext = createContext<WalletContextValue | undefined>(undefined);

export function useWallet() {
  const ctx = useContext(WalletContext);
  if (!ctx) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return ctx;
}
